import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ImageModule } from 'primeng/image';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { AuthService } from '../../../core/services/auth.service';
import { LoginRequest } from '../../../shared/models/auth.model';
import { LoaderComponent } from '../../../shared/components/ui/loader/loader.component';

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    ImageModule,
    InputTextModule,
    PasswordModule,
    CheckboxModule,
    ButtonModule,
    LoaderComponent,
  ],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.scss',
})
export class SignInComponent implements OnInit {
  loginForm: FormGroup;
  isLoading = false;
  errorMessage: string = '';
  /** Where to go after login; set from the `returnUrl` query param (e.g. a DocuSign return URL). */
  private returnUrl: string = '/dashboard';
  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      remember: [false]
    });
  }

  get password() {
    return this.loginForm.get('password');
  }

  ngOnInit() {
    // Preserve the URL the user was trying to reach (set by AuthGuard) so we return there after login.
    const requestedReturnUrl =
      this.route.snapshot.queryParamMap.get('returnUrl');
    if (requestedReturnUrl) {
      this.returnUrl = requestedReturnUrl;
    }

    // Never restore a saved password. A stale value here was submitted as the
    // typed password and produced intermittent "invalid credentials" errors.
    localStorage.removeItem('rememberedPassword');

    const rememberMe = localStorage.getItem('rememberMe');
    if (rememberMe === 'true') {
      this.loginForm.patchValue({
        remember: true
      });

      const userEmail = localStorage.getItem('userEmail');
      if (userEmail) {
        this.loginForm.patchValue({
          email: userEmail
        });
      }
    }
    // If rememberMe is false or doesn't exist, form remains empty (default state)
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const formValue = this.loginForm.value;
      const credentials: LoginRequest = {
        email: formValue.email,
        password: formValue.password,
      };

      this.authService.login(credentials).subscribe({
        next: (response) => {
          this.isLoading = false;

          const rememberMe = this.loginForm.get('remember')?.value;
          if (rememberMe === true || rememberMe === 'true') {
            localStorage.setItem('rememberMe', 'true');
          } else {
            localStorage.removeItem('rememberMe');
          }
          localStorage.removeItem('rememberedPassword');

          // Token is already stored in AuthService.
          // Return to the originally requested URL (e.g. DocuSign return URL) or the dashboard.
          this.router.navigateByUrl(this.returnUrl);
        },
        error: (err) => {
          this.isLoading = false;
          // Extract error message from the error object
          // Handle both Error objects and HttpErrorResponse
          let message = '';
          if (err instanceof Error) {
            message = err.message;
          } else if (err.error) {
            // Handle string or object error responses
            message = typeof err.error === 'string' ? err.error : err.error.message || err.message;
          } else {
            message = err.message || 'Invalid email or password. Please try again.';
          }
          this.errorMessage = message || 'Invalid email or password. Please try again.';

          setTimeout(() => {
            this.errorMessage = '';
          }, 5000);
        },
      });
    } else {
      // Mark all fields as touched to show validation errors
      this.loginForm.markAllAsTouched();
    }
  }
}
